'use client';

import Icon from '@/components/ui/AppIcon';

// ── Shared types & shape (also consumed by the orchestrator) ───────────────

export interface PermitBlockedRow {
  id: string;
  job_id: string;
  jurisdiction_name: string | null;
  status: string;
  filed_at: string | null;
  created_at: string;
  assigned_to_name: string | null;
}

export interface MaterialAlertRow {
  id: string;
  job_id: string;
  job_address: string | null;
  supplier_name: string;
  material_description: string;
  delivery_status: string;
  expected_delivery_date: string | null;
  created_at: string;
}

export interface ChangeOrderRow {
  id: string;
  job_id: string;
  job_address: string | null;
  customer_name: string;
  amount: number | string | null;
  status: string;
  requested_at: string | null;
  created_at: string;
}

export interface BottlenecksPayload {
  permits: PermitBlockedRow[];
  materials: MaterialAlertRow[];
  changeOrders: ChangeOrderRow[];
  counts: { permits: number; materials: number; changeOrders: number };
  totals: { changeOrderDollars: number };
}

interface Props {
  data: BottlenecksPayload | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

// ── Shared design tokens ───────────────────────────────────────────────────

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);
}

// ── Container ──────────────────────────────────────────────────────────────

export default function Row4_5OperationalBottlenecks({ data, loading, error, onRetry }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <PermitStatusWidget data={data} loading={loading} error={error} onRetry={onRetry} />
      <MaterialsStockWidget data={data} loading={loading} error={error} onRetry={onRetry} />
      <OpenChangeOrdersWidget data={data} loading={loading} error={error} onRetry={onRetry} />
    </section>
  );
}

// ── Shared sub-views: loading / error / empty ──────────────────────────────

function CardShell({
  title,
  icon,
  iconColor,
  subtitle,
  badge,
  children,
}: {
  title: string;
  icon: string;
  iconColor: string;
  subtitle?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name={icon} size={15} style={{ color: iconColor }} />
            <h3
              className="text-base font-bold"
              style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
            >
              {title}
            </h3>
          </div>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function LoadingBody() {
  return (
    <div className="py-6 flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-[2.5px] border-[#07B0E9] border-t-transparent animate-spin" />
    </div>
  );
}

function ErrorBody({ error, onRetry, tag }: { error: string; onRetry: () => void; tag: string }) {
  return (
    <div
      className="rounded-lg p-3 flex items-start gap-2"
      style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}
    >
      <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#B91C1C', flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold" style={{ color: '#7F1D1D' }}>
          {tag} fetch failed
        </p>
        <p className="text-[11px] text-gray-600 mt-0.5 break-words">{error}</p>
        <button
          onClick={onRetry}
          className="mt-2 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
          style={{ background: '#EF4444' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function EmptyBody({ message, icon = 'CheckCircleIcon' }: { message: string; icon?: string }) {
  return (
    <div
      className="rounded-lg p-3 flex items-center gap-2"
      style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)' }}
    >
      <Icon name={icon} size={16} style={{ color: '#10B981' }} />
      <p className="text-xs font-medium" style={{ color: '#047857' }}>
        {message}
      </p>
    </div>
  );
}

// ── 1. Permit Status ───────────────────────────────────────────────────────

const PERMIT_STATUS_LABEL: Record<string, string> = {
  filed: 'Filed',
  needs_filing: 'Needs filing',
  pending_review: 'Pending review',
  corrections_required: 'Corrections required',
};

function permitDaysColor(d: number): { color: string; bg: string } {
  if (d < 7)  return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (d < 22) return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
}

function PermitStatusWidget({ data, loading, error, onRetry }: Props) {
  if (loading && !data) {
    return (
      <CardShell title="Permit Status" icon="DocumentCheckIcon" iconColor="#F59E0B">
        <LoadingBody />
      </CardShell>
    );
  }
  if (error) {
    return (
      <CardShell title="Permit Status" icon="DocumentCheckIcon" iconColor="#F59E0B">
        <ErrorBody error={error} onRetry={onRetry} tag="[Permits]" />
      </CardShell>
    );
  }

  const rows = (data?.permits ?? [])
    .map((r) => ({ ...r, daysWaiting: daysSince(r.filed_at ?? r.created_at) }))
    .sort((a, b) => b.daysWaiting - a.daysWaiting);

  const over21 = rows.filter((r) => r.daysWaiting > 21).length;
  const total = rows.length;
  const top = rows.slice(0, 5);

  const badge = (
    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-bold"
        style={{ background: 'rgba(245,158,11,0.10)', color: '#92400E' }}
      >
        {total} blocked
      </span>
      {over21 > 0 && (
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C' }}
        >
          {over21} over 21d
        </span>
      )}
    </div>
  );

  return (
    <CardShell
      title="Permit Status"
      icon="DocumentCheckIcon"
      iconColor="#F59E0B"
      subtitle="Jobs blocked on permit"
      badge={badge}
    >
      {total === 0 ? (
        <EmptyBody message="No jobs blocked on permits" />
      ) : (
        <div className="space-y-2">
          {top.map((p) => {
            const c = permitDaysColor(p.daysWaiting);
            return (
              <div
                key={p.id}
                className="rounded-lg px-3 py-2"
                style={{ border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-mono text-gray-500">{p.job_id}</p>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
                    style={{ background: c.bg, color: c.color }}
                  >
                    {p.daysWaiting}d waiting
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800 truncate mt-0.5">
                  {p.jurisdiction_name || 'Unknown jurisdiction'}
                </p>
                <p className="text-[11px] text-gray-500 truncate">
                  {PERMIT_STATUS_LABEL[p.status] ?? p.status}
                  {p.assigned_to_name ? ` · ${p.assigned_to_name}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}

// ── 2. Materials Stock Alerts ──────────────────────────────────────────────

// ETA chip styling — past dates render red as "Was YYYY-MM-DD · Xd overdue".
function etaChip(eta: string | null): { text: string; color: string; bold: boolean } | null {
  if (!eta) return null;
  // eta is YYYY-MM-DD string. Compare as ISO date in UTC.
  const todayUtc = new Date().toISOString().slice(0, 10);
  if (eta < todayUtc) {
    const etaMs = new Date(`${eta}T00:00:00Z`).getTime();
    const todayMs = new Date(`${todayUtc}T00:00:00Z`).getTime();
    const daysOverdue = Math.max(0, Math.floor((todayMs - etaMs) / 86_400_000));
    return { text: `Was ${eta} · ${daysOverdue}d overdue`, color: '#B91C1C', bold: true };
  }
  return { text: `ETA ${eta}`, color: '#6B7280', bold: false };
}

function materialSeverity(status: string): { label: string; color: string; bg: string; weight: number } {
  if (status === 'delayed') return { label: 'Critical', color: '#B91C1C', bg: 'rgba(239,68,68,0.15)', weight: 2 };
  // pending, low_stock, rush, contacted all surface as Watch.
  if (status === 'pending' || status === 'low_stock' || status === 'rush' || status === 'contacted') {
    return { label: 'Watch', color: '#92400E', bg: 'rgba(245,158,11,0.15)', weight: 1 };
  }
  return { label: status, color: '#6B7280', bg: 'rgba(107,114,128,0.10)', weight: 0 };
}

function MaterialsStockWidget({ data, loading, error, onRetry }: Props) {
  if (loading && !data) {
    return (
      <CardShell title="Materials Stock Alerts" icon="CubeIcon" iconColor="#EF4444">
        <LoadingBody />
      </CardShell>
    );
  }
  if (error) {
    return (
      <CardShell title="Materials Stock Alerts" icon="CubeIcon" iconColor="#EF4444">
        <ErrorBody error={error} onRetry={onRetry} tag="[Materials]" />
      </CardShell>
    );
  }

  const rows = (data?.materials ?? [])
    .map((r) => ({ ...r, sev: materialSeverity(r.delivery_status) }))
    .sort((a, b) => b.sev.weight - a.sev.weight);

  const total = rows.length;
  const criticalCount = rows.filter((r) => r.sev.label === 'Critical').length;
  const top = rows.slice(0, 5);

  const badge = (
    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-bold"
        style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C' }}
      >
        {total} alerts
      </span>
      {criticalCount > 0 && (
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ background: 'rgba(239,68,68,0.15)', color: '#7F1D1D' }}
        >
          {criticalCount} critical
        </span>
      )}
    </div>
  );

  return (
    <CardShell
      title="Materials Stock Alerts"
      icon="CubeIcon"
      iconColor="#EF4444"
      subtitle="Delayed or pending deliveries"
      badge={badge}
    >
      {total === 0 ? (
        <EmptyBody message="No material alerts — all deliveries on track" />
      ) : (
        <div className="space-y-2">
          {top.map((m) => (
            <div
              key={m.id}
              className="rounded-lg px-3 py-2"
              style={{ border: '1px solid #F3F4F6' }}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-mono text-gray-500">{m.job_id}</p>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
                  style={{ background: m.sev.bg, color: m.sev.color }}
                >
                  {m.sev.label}
                </span>
              </div>
              <p className="text-sm font-semibold text-gray-800 truncate mt-0.5">
                {m.material_description}
              </p>
              <p className="text-[11px] text-gray-500 truncate">
                {m.supplier_name}
                {(() => {
                  const eta = etaChip(m.expected_delivery_date);
                  if (!eta) return null;
                  return (
                    <>
                      {' · '}
                      <span style={{ color: eta.color, fontWeight: eta.bold ? 600 : 400 }}>
                        {eta.text}
                      </span>
                    </>
                  );
                })()}
              </p>
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
}

// ── 3. Open Change Orders ──────────────────────────────────────────────────

// Truncate (not round) to 1 decimal in thousands so $6,950 shows "$6.9k" not "$7.0k".
// toFixed() rounds half-to-even with FP edge cases; floor gives a consistent floor.
function formatDollars(n: number): string {
  if (n >= 1000) {
    const truncatedTenths = Math.floor(n / 100) / 10;
    return `$${truncatedTenths.toFixed(1)}k`;
  }
  return `$${n.toFixed(0)}`;
}

function OpenChangeOrdersWidget({ data, loading, error, onRetry }: Props) {
  if (loading && !data) {
    return (
      <CardShell title="Open Change Orders" icon="DocumentTextIcon" iconColor="#07B0E9">
        <LoadingBody />
      </CardShell>
    );
  }
  if (error) {
    return (
      <CardShell title="Open Change Orders" icon="DocumentTextIcon" iconColor="#07B0E9">
        <ErrorBody error={error} onRetry={onRetry} tag="[ChangeOrders]" />
      </CardShell>
    );
  }

  // Server already sorted by created_at ASC (oldest first).
  const rows = data?.changeOrders ?? [];
  const total = rows.length;
  const totalDollars = data?.totals.changeOrderDollars ?? 0;
  const top = rows.slice(0, 5);

  const badge = (
    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-bold"
        style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
      >
        {total} open
      </span>
      {totalDollars > 0 && (
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ background: 'rgba(3,52,148,0.10)', color: '#033494' }}
        >
          {formatDollars(totalDollars)} at risk
        </span>
      )}
    </div>
  );

  return (
    <CardShell
      title="Open Change Orders"
      icon="DocumentTextIcon"
      iconColor="#07B0E9"
      subtitle="Awaiting customer signature"
      badge={badge}
    >
      {total === 0 ? (
        <EmptyBody message="No change orders awaiting signature" />
      ) : (
        <div className="space-y-2">
          {top.map((co) => {
            const days = daysSince(co.created_at);
            const amount = Number(co.amount ?? 0);
            return (
              <div
                key={co.id}
                className="rounded-lg px-3 py-2"
                style={{ border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-mono text-gray-500">{co.job_id}</p>
                  <span
                    className="text-xs font-bold whitespace-nowrap"
                    style={{ color: '#033494' }}
                  >
                    {formatDollars(amount)}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800 truncate mt-0.5">
                  {co.customer_name}
                </p>
                <p className="text-[11px] text-gray-500 truncate">
                  {days}d pending{co.job_address ? ` · ${co.job_address}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}
